import { Suspense, type ReactElement, type ReactNode } from 'react';
import { Await } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SCAPIShopperStoresSchemas } from 'src/types';
import { DynamicImage } from '@/components/dynamic-image';
import type { DynamicImageDimensions } from '@/lib/images/dynamic-image';
import { Link, NewTabAnchor } from '@/components/link';
import { SvgPhone, SvgEmail, SvgLocation } from '@/components/icons-eataly';
import { focusVisibleOutline } from '@/lib/focus-styles';
import { buildStoreDirectionsUrl, formatStoreAddress } from './event-course-store-format';
import { cn } from '@/lib/utils';
import { resolveImageAlt } from '@/lib/images/resolve-image-alt';

type Store = SCAPIShopperStoresSchemas['Store'];

interface EventCourseLocationTabProps {
    /** Deferred store linked to the product via `c_eventStoreId`. */
    eventStore: Promise<Store | undefined>;
}

/** Store image tile: full width on mobile, ~one column of the two-column desktop grid. */
const STORE_IMAGE_WIDTHS: DynamicImageDimensions = { base: '100vw', lg: '42vw', '2xl': 600 };

const sectionLabelClassName = 'label-l uppercase text-neutral-40';

const contactLinkClassName = cn(
    'body-copy-m-bold flex items-center gap-3 text-neutral-10 underline underline-offset-4',
    focusVisibleOutline
);

/** Small grey uppercase heading above each block (LOCATION / CONTATTI). */
function SectionLabel({ children }: { children: ReactNode }): ReactElement {
    return <p className={sectionLabelClassName}>{children}</p>;
}

/** Location block: store image, name and the "scopri la sede" CTA. */
function StoreLocation({ store }: { store: Store }): ReactElement {
    const { t } = useTranslation('product');
    const address = formatStoreAddress(store);

    return (
        <div className="col-span-full lg:col-span-2 flex flex-col gap-4">
            <SectionLabel>{t('eventCourse.location.locationLabel')}</SectionLabel>
            {store.image && (
                <div className="w-full overflow-hidden rounded-lg aspect-square">
                    <DynamicImage
                        src={store.image}
                        alt={resolveImageAlt({ name: store.name })}
                        className="h-auto w-full"
                        loading="lazy"
                        widths={STORE_IMAGE_WIDTHS}
                    />
                </div>
            )}
            {store.name && <p className="body-copy-m-bold text-neutral-10">{store.name}</p>}
            {/* TODO: point to the store detail page once the store route/URL is defined. */}
            <Link
                to="#"
                className={cn('label-l flex w-fit items-center gap-2 uppercase text-neutral-10', focusVisibleOutline)}
                aria-label={t('eventCourse.location.discoverStoreAriaLabel', { storeName: store.name ?? address })}>
                {t('eventCourse.location.discoverStore')}
                <ArrowRight className="size-5" aria-hidden />
            </Link>
        </div>
    );
}

/** Contacts block: phone, email and address. */
function StoreContacts({ store }: { store: Store }): ReactElement {
    const { t } = useTranslation('product');
    const address = formatStoreAddress(store);
    const directionsUrl = buildStoreDirectionsUrl(store);

    if (!store.phone && !store.email && !address) {
        return <></>;
    }

    return (
        <div className="col-span-full lg:col-span-8 lg:col-start-4 flex flex-col gap-4">
            <SectionLabel>{t('eventCourse.location.contactsLabel')}</SectionLabel>
            <ul className="flex flex-col gap-4">
                {store.phone && (
                    <li>
                        <a href={`tel:${store.phone.replace(/\s+/g, '')}`} className={contactLinkClassName}>
                            <SvgPhone className="size-5 shrink-0 text-neutral-10" aria-hidden />
                            <span>{store.phone}</span>
                        </a>
                    </li>
                )}
                {store.email && (
                    <li>
                        <a href={`mailto:${store.email}`} className={contactLinkClassName}>
                            <SvgEmail className="size-5 shrink-0 text-neutral-10" aria-hidden />
                            <span>{store.email}</span>
                        </a>
                    </li>
                )}
                {address && (
                    <li className="body-copy-m flex items-center gap-3 text-neutral-10">
                        {directionsUrl ? (
                            <NewTabAnchor
                                href={directionsUrl}
                                accessibleName={address}
                                className={contactLinkClassName}>
                                <SvgLocation className="size-5 shrink-0 text-neutral-10" aria-hidden />
                                <span className="underline underline-offset-4">{address}</span>
                            </NewTabAnchor>
                        ) : (
                            <span className="flex items-center gap-3">
                                <SvgLocation className="size-5 shrink-0 text-neutral-10" aria-hidden />
                                <span>{address}</span>
                            </span>
                        )}
                    </li>
                )}
            </ul>
        </div>
    );
}

/** Two-column content once the store resolves: location left, contacts right. */
function StoreContent({ store }: { store: Store }): ReactElement | null {
    // With no usable store data there is nothing to show; render nothing rather than empty headings.
    if (!store.image && !store.name && !store.phone && !store.email && !formatStoreAddress(store)) {
        return null;
    }

    return (
        <div className="grid grid-cols-6 pb-12 lg:grid-cols-10 gap-1">
            <StoreLocation store={store} />
            <StoreContacts store={store} />
        </div>
    );
}

/** Skeleton fallback while the deferred store promise resolves (below the fold). */
function StoreContentSkeleton(): ReactElement {
    return (
        <div className="grid animate-pulse grid-cols-1 gap-8 pb-12 lg:grid-cols-2 lg:gap-12">
            <div className="col-span-full lg:col-span-2 flex flex-col gap-4">
                <div className="h-3 w-24 rounded bg-sand-95" />
                <div className="aspect-[4/3] w-full rounded-lg bg-sand-95" />
                <div className="h-5 w-40 rounded bg-sand-95" />
            </div>
            <div className="col-span-full lg:col-span-8 lg:col-start-4 flex flex-col gap-4">
                <div className="h-3 w-24 rounded bg-sand-95" />
                <div className="h-5 w-48 rounded bg-sand-95" />
                <div className="h-5 w-56 rounded bg-sand-95" />
                <div className="h-5 w-52 rounded bg-sand-95" />
            </div>
        </div>
    );
}

/**
 * "Location e contatti" tab content for event/course PDPs. The store is loaded from the
 * product's `c_eventStoreId` via the Shopper Stores API as a deferred (non-critical) promise;
 * this component suspends on it and renders nothing if no store is associated or the fetch failed.
 */
export default function EventCourseLocationTab({ eventStore }: EventCourseLocationTabProps): ReactElement {
    return (
        <Suspense fallback={<StoreContentSkeleton />}>
            <Await resolve={eventStore}>{(store) => (store ? <StoreContent store={store} /> : null)}</Await>
        </Suspense>
    );
}
